import queryString from 'query-string'
import ApolloClient from 'apollo-client'
import { HttpLink } from 'apollo-link-http'
import { ApolloLink } from 'apollo-link'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { authorizeTrialOrgUser, handleNewUser, logout, validateToken } from './services/login'

const guessOSAddress = (isTablet) => {
  const productionBackendHostname = 'collab.glue.work'
  const stagingBackendHostname = 'staging.glue.work'
  const testEnvBackendHostName = 'dev.glue.work'

  if (window.location.hostname === productionBackendHostname) {
    return isTablet ? 'https://glueos.glue.work' : 'https://web.glue.work'
  } else if (window.location.hostname === stagingBackendHostname) {
    return isTablet ? 'https://glueos.internal.glue.work' : 'https://webstaging.glue.work'
  } else if (window.location.hostname === testEnvBackendHostName) {
    return 'https://webdev.glue.work'
  } else {
    return null
  }
}

export const initLoginFlow = async () => {
  const hashParams = queryString.parse(document.location.hash)
  const searchParams = (new URL(document.location)).searchParams

  const trialOrgConfirmation = searchParams.get('registration_confirmation_token')
  const newUser = searchParams.get('acceptInvitation')
  const newUserOrgId = searchParams.get('orgId')

  const redirUri = searchParams.get('redirectUri')
  let ui = window.sessionStorage.getItem('ui')
  if (redirUri) {
    const searchRduParams = (new URL(redirUri)).searchParams
    ui = searchRduParams.get('ui')
  }
  const isTablet = ui === 'tablet'

  if (isTablet) {
    window.sessionStorage.setItem('ui', 'tablet')
  } else {
    window.sessionStorage.setItem('ui', 'web')
  }

  if (trialOrgConfirmation) {
    console.log('TRIAL ORG AUTORIZING USER!')
    authorizeTrialOrgUser(trialOrgConfirmation)
  }

  if (newUser) {
    handleNewUser({ acceptInvitation: newUser, orgId: newUserOrgId })
  }

  const wasLogout = searchParams.get('logoutComplete')
  console.log('initialization.js: wasLogout = ', wasLogout, ' isTablet = ', isTablet)

  if (wasLogout && isTablet) {
    if (!window.vuplex) {
      console.log('Waiting for Vuplex to initialize...')
      await new Promise((resolve, reject) => {
        console.log('Vuplex was not ready; Hook an event listener')
        window.addEventListener('vuplexready', () => resolve())
      })
    }

    if (window.vuplex) {
      window.vuplex.postMessage({ topic: 'Log out', args: null })
      window.removeEventListener('vuplexready')
      return
    } else {
      console.log('Vuplex is not enabled.')
    }
  }

  const glueOSUri = searchParams.get('redirectUri') ?? window.sessionStorage.getItem('glueOSUri') ?? guessOSAddress(true) ?? null
  if (glueOSUri) {
    window.sessionStorage.setItem('glueOSUri', glueOSUri)
  }

  const idToken = hashParams.id_token ?? searchParams.get('id_token') ?? window.localStorage.getItem('idToken') ?? null

  let validIdToken = false
  if (idToken) {
    // Need to set token to storage so the authorization header is correct for web request
    window.localStorage.setItem('idToken', idToken)
    const tokenValidationRes = await validateToken(idToken)
    validIdToken = !!tokenValidationRes && tokenValidationRes.tokenValid
  }

  if (!validIdToken) {
    window.localStorage.removeItem('idToken')
  }

  if (validIdToken && searchParams.get('logout')) {
    logout()
    return
  }

  if (validIdToken && window.vuplex) {
    window.vuplex.postMessage({ authToken: idToken })
  }

  if (validIdToken && glueOSUri) {
    const redirectUrl = new URL(glueOSUri)
    const backendUrl = redirectUrl.searchParams.get('backend-url')
    if (!backendUrl) {
      redirectUrl.searchParams.append('backend-url', window.location.protocol + '//' + window.location.host)
    }
    redirectUrl.hash = '#id_token=' + idToken
    window.location = redirectUrl
    return
  }

  const httpLink = new HttpLink({ uri: '/api/graphql' })
  const authLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        Authorization: 'Bearer ' + idToken
      }
    })
    return forward(operation)
  })
  const link = authLink.concat(httpLink)

  const apollo = new ApolloClient({
    link: link,
    cache: new InMemoryCache().restore(window.__APOLLO_STATE__)
  })

  return { apollo: apollo }
}
